import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, Star, ArrowRight } from 'lucide-react';
import { useOrganizacion } from '../context/OrganizacionContext';

const RoleSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config, logoUrl } = useOrganizacion();
  
  // Obtener información del usuario desde localStorage
  const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
  
  const handleSelectRole = (role) => {
    // Actualizar el tipo de usuario en localStorage según la selección
    const updatedUserInfo = {
      ...userInfo,
      tipo: role === 'lider' ? 'lider' : 'employee'
    };
    
    localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
    
    // Redirigir al dashboard correspondiente
    if (role === 'lider') {
      navigate('/dashboard-lider');
    } else {
      navigate('/empleado/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Logo y título */}
        <div className="text-center mb-8">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt={config?.nombre_organizacion || 'Logo'} 
              className="h-16 mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenido, {userInfo.nombre}
          </h1>
          <p className="text-gray-600">
            Selecciona cómo deseas acceder al sistema
          </p>
        </div>

        {/* Opciones de rol */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Opción: Dashboard de Líder */}
          <div 
            onClick={() => handleSelectRole('lider')}
            className="bg-white rounded-xl shadow-lg p-8 cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl border-2 border-transparent hover:border-blue-500"
          >
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-100 rounded-full p-4 mb-4">
                <Users className="w-12 h-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Dashboard de Líder
              </h2>
              <p className="text-gray-600 mb-4">
                Evalúa a tu equipo de trabajo
              </p>
              <ul className="text-left text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                  <span>Evaluar a {userInfo.empleados_a_cargo?.length || 0} empleados a tu cargo</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                  <span>Ver estadísticas de tu equipo</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                  <span>Gestionar evaluaciones pendientes</span>
                </li>
              </ul>
              <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Acceder como Líder
              </button>
            </div>
          </div>

          {/* Opción: Dashboard de Empleado (360°) */}
          <div 
            onClick={() => handleSelectRole('empleado')}
            className="bg-white rounded-xl shadow-lg p-8 cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl border-2 border-transparent hover:border-purple-500"
          >
            <div className="flex flex-col items-center text-center">
              <div className="bg-purple-100 rounded-full p-4 mb-4">
                <Star className="w-12 h-12 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Evaluación 360°
              </h2>
              <p className="text-gray-600 mb-4">
                Evalúa a tu líder directo
              </p>
              <ul className="text-left text-sm text-gray-600 space-y-2 mb-6">
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 mr-2 mt-0.5 text-purple-500 flex-shrink-0" />
                  <span>Evaluar el liderazgo de tu jefe</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 mr-2 mt-0.5 text-purple-500 flex-shrink-0" />
                  <span>Proporcionar feedback constructivo</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-4 h-4 mr-2 mt-0.5 text-purple-500 flex-shrink-0" />
                  <span>Contribuir a la mejora organizacional</span>
                </li>
              </ul>
              <button className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors">
                Acceder como Empleado
              </button>
            </div>
          </div>
        </div>

        {/* Nota informativa */}
        <div className="mt-8 bg-white rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Nota:</span> Como líder intermedio, puedes acceder a ambos dashboards en cualquier momento.
            Puedes cambiar entre roles desde el menú de usuario.
          </p>
        </div>

        {/* Ayuda y contacto */}
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500 mb-2">¿Necesitas ayuda?</p>
          <a
            href="mailto:cesar.palacios@aossas.com?subject=Soporte - Sistema de Evaluación 360°&body=Hola, necesito ayuda con el sistema de evaluación."
            className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contacta al administrador del sistema
          </a>
        </div>
      </div>
    </div>
  );
};

export default RoleSelection;
